import React, { useRef, useEffect, useMemo, useState } from "react";
import { Formik, Form, FormikProps } from "formik";
import * as Yup from "yup";
import { useSelector, useDispatch } from "react-redux";
import { createColumnHelper } from "@tanstack/react-table";
import SlideOutSidebar from "../../ui-components/slide-out-sidebar/slideOutSidebar";
import { LabelModes } from "../../ui-components/molecules/labeled-inputs/labeledInput";
import Button, { ButtonThemes } from "../../ui-components/button/button";
import { XGSSelectOption } from "../../ui-components/xgs-select/xgsSelect";
import { getTerminalsList, terminalSummarySelector } from "../../slices/terminal-summary/terminalSummarySlice";
import TerminalSummaryState from "../../slices/terminal-summary/TerminalSummaryState";
import Table from "../../ui-components/table/table";
import "./addEditLocation.scss";
import XGSFormInput from "../../ui-components/form/input/xgsFormInput";
import XGSFormSelect from "../../ui-components/form/select/xgsFormSelect";
import { SecondaryDistributor } from "../../app/data/door-master/models";
import AddSecondaryDistributorModal, { SecondaryDistributorItem } from "./addSecondaryDistributorModal";

interface AddEditLocationProps {
  show: boolean;
  onClose: () => void;
  onSave: (values: LocationFormValues) => void;
  location?: any;
  isMutating?: boolean;
}

interface LocationFormValues {
  terminalNumber: string;
  locationCode: string;
  locationType: string;
  subType: string;
  priority: string;
  description: string;
  distributorNumber: string;
  distributorName: string;
  status: string;
  usage: string;
  trailer: string;
  carrier: string;
  isActive: string;
  secondaryDistributors: SecondaryDistributor[];
}

const LOCATION_TYPE_OPTIONS: XGSSelectOption[] = [
  { label: "DOOR", value: "DOOR" },
  { label: "STAGING", value: "STAGING" },
  { label: "BIN", value: "BIN" },
  { label: "RACK", value: "RACK" },
  { label: "TEMPORARY", value: "TEMPORARY" },
];

const SUB_TYPE_OPTIONS_DOOR: XGSSelectOption[] = [
  { label: "Loading Door", value: "LOADING_DOOR" },
  { label: "Unloading Door", value: "UNLOADING_DOOR" },
  { label: "City Delivery Door", value: "CITY_DELIVERY_DOOR" },
];

const SUB_TYPE_OPTIONS_STAGING: XGSSelectOption[] = [
  { label: "Staging Area", value: "STAGING_AREA" },
];

const SUB_TYPE_OPTIONS_BIN: XGSSelectOption[] = [
  { label: "Storage Bin", value: "STORAGE_BIN" },
];

const SUB_TYPE_OPTIONS_RACK: XGSSelectOption[] = [
  { label: "Storage Bin", value: "STORAGE_BIN" },
];

const SUB_TYPE_OPTIONS_TEMPORARY: XGSSelectOption[] = [
  { label: "Temporary Area", value: "TEMPORARY_AREA" },
];

const STATUS_OPTIONS: XGSSelectOption[] = [
  { label: "Normal", value: "NORMAL" },
  { label: "Overweight", value: "OVERWEIGHT" },
  { label: "Return", value: "RETURN" },
  { label: "Linehaul", value: "LINEHAUL" },
  { label: "Blocked", value: "BLOCKED" },
];

const USAGE_OPTIONS: XGSSelectOption[] = [
  { label: "Permanent", value: "PERMANENT" },
  { label: "City Route", value: "CITY_ROUTE" },
  { label: "Will Call", value: "WILL_CALL" },
];

const ACTIVE_OPTIONS: XGSSelectOption[] = [
  { label: "Active", value: "true" },
  { label: "Inactive", value: "false" },
];

const validationSchema = Yup.object().shape({
  terminalNumber: Yup.string().required("Terminal is required"),
  locationCode: Yup.string().required("Location code is required"),
  locationType: Yup.string().required("Location type is required"),
  isActive: Yup.string().required("Status is required"),
});

// Extended type for UI display (includes extra fields for table)
interface SecondaryDistributorDisplay extends SecondaryDistributor {
  id: string;
  attention?: string;
  address?: string;
}

const columnHelper = createColumnHelper<SecondaryDistributorDisplay>();

const AddEditLocation: React.FC<AddEditLocationProps> = ({ show, onClose, onSave, location, isMutating = false }) => {
  const dispatch = useDispatch();
  const terminalSummaryState: TerminalSummaryState = useSelector(terminalSummarySelector);
  const formikRef = useRef<FormikProps<LocationFormValues>>(null);
  
  // State for secondary distributors and modal
  const [secondaryDistributors, setSecondaryDistributors] = useState<SecondaryDistributorDisplay[]>([]);
  const [showAddDistributorModal, setShowAddDistributorModal] = useState(false);
  
  // Get terminals list
  useEffect(() => {
    dispatch(getTerminalsList(0, null, null, "ACTIVE", "XGS Terminal", true));
  }, [dispatch]);

  const terminalOptions = useMemo(() => {
    if (terminalSummaryState.terminallist) {
      return terminalSummaryState.terminallist.map((terminal) => ({
        label: terminal.terminalName,
        value: terminal.terminalNumber.toString(),
      }));
    }
    return [];
  }, [terminalSummaryState.terminallist]);

  // Primary distributor options (same as terminals)
  const primaryDistributorOptions: XGSSelectOption[] = useMemo(() => {
    if (terminalSummaryState.terminallist) {
      return terminalSummaryState.terminallist.map((terminal) => ({
        label: `${terminal.terminalName} (${terminal.terminalNumber})`,
        value: terminal.terminalNumber.toString(),
      }));
    }
    return [];
  }, [terminalSummaryState.terminallist]);

  const initialValues: LocationFormValues = {
    terminalNumber: location?.terminalNumber
      ? terminalOptions.find((t) => t.value === location.terminalNumber.toString())?.value || ""
      : "",
    locationCode: location?.locationCode || "",
    locationType: location?.locationType || "",
    subType: location?.subType || "",
    priority: location?.priority || "",
    description: location?.description || "",
    distributorNumber: location?.distributorNumber?.toString() || "",
    distributorName: location?.distributorName || "",
    status: location?.status || STATUS_OPTIONS[0].value,
    usage: location?.usage || "",
    trailer: location?.trailer || "",
    carrier: location?.carrier || "",
    isActive: ((): string => {
      if (location?.isActive !== undefined) {
        const activeValue = String(location.isActive);
        const found = ACTIVE_OPTIONS.find((o) => o.value === activeValue);
        return (found ? found.value : ACTIVE_OPTIONS[0].value) as string;
      }
      return ACTIVE_OPTIONS[0].value as string;
    })(),
    secondaryDistributors: location?.secondaryDistributors || [],
  };

  const getSubTypeOptions = (locationType: string): XGSSelectOption[] => {
    switch (locationType) {
      case "DOOR":
        return SUB_TYPE_OPTIONS_DOOR;
      case "STAGING":
        return SUB_TYPE_OPTIONS_STAGING;
      case "BIN":
        return SUB_TYPE_OPTIONS_BIN;
      case "RACK":
        return SUB_TYPE_OPTIONS_RACK;
      case "TEMPORARY":
        return SUB_TYPE_OPTIONS_TEMPORARY;
      default:
        return [];
    }
  };

  const handleSubmit = (values: LocationFormValues) => {
    // Convert display format back to API format
    const apiDistributors: SecondaryDistributor[] = secondaryDistributors.map(d => ({
      distributorNumber: d.distributorNumber,
      distributorName: d.distributorName,
    }));
    
    onSave({
      ...values,
      secondaryDistributors: apiDistributors,
    });
  };

  const handleClose = () => {
    formikRef.current?.resetForm();
    setSecondaryDistributors([]);
    onClose();
  };

  // Reset form and load secondary distributors when sidebar opens/closes
  useEffect(() => {
    if (show) {
      // Initialize secondary distributors
      if (location?.secondaryDistributors) {
        const displayDistributors: SecondaryDistributorDisplay[] = location.secondaryDistributors.map(
          (d: SecondaryDistributor, index: number) => ({
            id: d.distributorNumber || `temp-${index}`,
            distributorNumber: d.distributorNumber,
            distributorName: d.distributorName,
            attention: "-",
            address: "-",
          })
        );
        setSecondaryDistributors(displayDistributors);
      }
    } else {
      formikRef.current?.resetForm();
      setSecondaryDistributors([]);
    }
  }, [show, location]);

  // Handle removing a secondary distributor
  const handleRemoveDistributor = (id: string) => {
    setSecondaryDistributors(prev => prev.filter(d => d.id !== id));
  };

  // Handle adding a secondary distributor from modal
  const handleAddDistributor = (distributor: SecondaryDistributorItem) => {
    const newDistributor: SecondaryDistributorDisplay = {
      id: distributor.id,
      distributorNumber: distributor.distributorNumber,
      distributorName: distributor.distributorName,
      attention: distributor.attention,
      address: distributor.address,
    };
    setSecondaryDistributors(prev => [...prev, newDistributor]);
  };

  // Table columns for secondary distributors
  const secondaryDistributorColumns = useMemo(() => [
    columnHelper.accessor("distributorNumber", {
      id: "distributorNumber",
      header: "Account#",
      size: 80,
    }),
    columnHelper.accessor("distributorName", {
      id: "distributorName",
      header: "Name",
      size: 240,
    }),
    // columnHelper.accessor("attention", {
    //   id: "attention",
    //   header: "Attention",
    //   size: 75,
    // }),
    // columnHelper.accessor("address", {
    //   id: "address",
    //   header: "Address",
    //   size: 115,
    // }),
    columnHelper.display({
      id: "action",
      header: "Action",
      size: 63,
      cell: (cellProps) => (
        <span
          onClick={() => handleRemoveDistributor(cellProps.row.original.id)}
          style={{ color: 'red', cursor: 'pointer', fontSize: '12px' }}
        >
          Remove
        </span>
      ),
    }),
  ], []);

  return (
    <>
    <SlideOutSidebar
      show={show}
      onClose={handleClose}
      size="small"
      header={location ? "Edit Location" : "Create Location"}
    >
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        innerRef={formikRef}
        enableReinitialize
      >
        {(formikProps: FormikProps<LocationFormValues>) => {
          const subTypeOptions = getSubTypeOptions(formikProps.values.locationType || "");
          return (
            <Form className="xgs-add-edit-location">
              <div className="xgs-add-edit-location__fields">
                <XGSFormSelect
                  name="terminalNumber"
                  label="Terminal"
                  labelMode={LabelModes.column}
                  placeholder="Select Terminal"
                  required
                  options={terminalOptions}
                  controlled
                  className="xgs-add-edit-location__field"
                />
                <div className="xgs-add-edit-location__row">
                  <XGSFormInput
                    name="locationCode"
                    label="Location Code"
                    labelMode={LabelModes.column}
                    placeholder="e.g., 001, BACK, HOD"
                    required
                    className="xgs-add-edit-location__field"
                  />
                  <XGSFormInput
                    name="priority"
                    label="Priority"
                    labelMode={LabelModes.column}
                    placeholder="1-9"
                    className="xgs-add-edit-location__field"
                  />
                </div>

                <div className="xgs-add-edit-location__row">
                  <XGSFormSelect
                    name="locationType"
                    label="Location Type"
                    labelMode={LabelModes.column}
                    placeholder="Select Location Type"
                    required
                    options={LOCATION_TYPE_OPTIONS}
                    controlled
                    onValueChange={(option) => {
                      formikProps.setFieldValue("locationType", option?.value || "");
                      formikProps.setFieldValue("subType", "");
                    }}
                    className="xgs-add-edit-location__field"
                  />
                  <XGSFormSelect
                    name="subType"
                    label="Sub Type"
                    labelMode={LabelModes.column}
                    placeholder="Select Sub Type"
                    options={subTypeOptions}
                    controlled
                    className="xgs-add-edit-location__field"
                    isDisabled={!formikProps.values.locationType}
                  />
                </div>

                <XGSFormInput
                  name="description"
                  label="Description"
                  labelMode={LabelModes.column}
                  placeholder="Optional text description for the location"
                  className="xgs-add-edit-location__field"
                />

                <XGSFormSelect
                  name="distributorNumber"
                  label="Primary Distributor"
                  labelMode={LabelModes.column}
                  placeholder="Select Primary Distributor"
                  options={primaryDistributorOptions}
                  controlled
                  isClearable
                  onValueChange={(option) => {
                    formikProps.setFieldValue("distributorNumber", option?.value || "");
                    if (option && terminalSummaryState.terminallist) {
                      const terminal = terminalSummaryState.terminallist.find(
                        t => t.terminalNumber.toString() === option.value
                      );
                      formikProps.setFieldValue("distributorName", terminal?.terminalName || "");
                    } else {
                      formikProps.setFieldValue("distributorName", "");
                    }
                  }}
                  className="xgs-add-edit-location__field"
                />

                {/* Secondary Distributors Section */}
                <div className="xgs-add-edit-location__section">
                  <div className="xgs-add-edit-location__section-title">
                    Secondary Distributors
                  </div>
                  
                  {secondaryDistributors.length > 0 && (
                    <div className="xgs-add-edit-location__table">
                      <Table
                        columns={secondaryDistributorColumns}
                        data={secondaryDistributors}
                        noResultsText="No secondary distributors"
                      />
                    </div>
                  )}

                  <div 
                    className="xgs-add-edit-location__add-button"
                    onClick={secondaryDistributors.length < 4 ? () => setShowAddDistributorModal(true) : undefined}
                    style={{ 
                      opacity: secondaryDistributors.length >= 4 ? 0.5 : 1,
                      cursor: secondaryDistributors.length >= 4 ? 'not-allowed' : 'pointer'
                    }}
                  >
                    + Add Secondary Distributors {secondaryDistributors.length >= 4 && '(Max 4)'}
                  </div>
                </div>

                <div className="xgs-add-edit-location__row">
                  <XGSFormSelect
                    name="status"
                    label="Status"
                    labelMode={LabelModes.column}
                    placeholder="Select Status"
                    options={STATUS_OPTIONS}
                    controlled
                    className="xgs-add-edit-location__field"
                  />
                  <XGSFormSelect
                    name="usage"
                    label="Usage"
                    labelMode={LabelModes.column}
                    placeholder="Select Usage"
                    options={USAGE_OPTIONS}
                    controlled
                    className="xgs-add-edit-location__field"
                  />
                </div>

                <XGSFormSelect
                  name="isActive"
                  label="Is Active"
                  labelMode={LabelModes.column}
                  placeholder="Status"
                  required
                  options={ACTIVE_OPTIONS}
                  controlled
                  className="xgs-add-edit-location__field"
                />
              </div>

              <div className="xgs-add-edit-location__actions">
                <Button theme={ButtonThemes.gray} onClick={handleClose} type="button">
                  Cancel
                </Button>
                <Button 
                  spinner={isMutating} 
                  theme={ButtonThemes.blue} 
                  type="submit"
                >
                  Save Location
                </Button>
              </div>
            </Form>
          );
        }}
      </Formik>
    </SlideOutSidebar>

    <AddSecondaryDistributorModal
      show={showAddDistributorModal}
      onClose={() => setShowAddDistributorModal(false)}
      onAdd={handleAddDistributor}
      existingDistributorIds={secondaryDistributors.map(d => d.id)}
    />
    </>
  );
};

export default AddEditLocation;

